import { useEffect, useRef, useState } from 'react';
import { GlowOrb } from './NavBar';
import { useCountUp } from '@/hooks/useCountUp';

function AnimatedStat({ value, unit, label, decimals = 0, prefix = '', enabled = false }: {
  value: number; unit: string; label: string; decimals?: number; prefix?: string; enabled?: boolean;
}) {
  const count = useCountUp(value, 2000, enabled, decimals);
  return (
    <div className="p-5 rounded-xl border border-background-800/60 bg-background-900/40 text-center card-lift">
      <div className="flex items-baseline justify-center gap-1 mb-2">
        <span className="text-2xl md:text-3xl font-bold text-primary-400 font-heading">{prefix}{count}</span>
        <span className="text-sm text-primary-400/70">{unit}</span>
      </div>
      <p className="text-xs text-foreground-500">{label}</p>
    </div>
  );
}

export default function ArchitectureSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const layers = [
    { name: '认知层', desc: '意图理解 · 决策推理 · 知识图谱', color: 'bg-primary-500/20 text-primary-300 border-primary-500/30' },
    { name: '交互层', desc: 'AR 显示 · 语音交互 · 手势识别', color: 'bg-accent-500/15 text-accent-300 border-accent-500/25' },
    { name: '感知层', desc: '视觉 SLAM · 激光雷达 · 语义分割', color: 'bg-primary-500/15 text-primary-300 border-primary-500/25' },
    { name: '执行层', desc: '路径规划 · 运动控制 · 任务执行', color: 'bg-secondary-500/15 text-secondary-300 border-secondary-500/25' },
    { name: '基础设施', desc: '空间坐标系 · 数据同步 · 安全合规', color: 'bg-background-800 text-foreground-300 border-background-700' },
  ];

  const connectorLabels = [
    { left: '意图翻译', right: '指令下发' },
    { left: '空间标注', right: '坐标转换' },
    { left: '语义理解', right: '路径规划' },
    { left: '状态上报', right: '数据同步' },
  ];

  const stats = [
    { value: 11, unit: '台', label: '异构终端接入', decimals: 0, prefix: '' },
    { value: 8420, unit: '㎡', label: '实时覆盖面积', decimals: 0, prefix: '' },
    { value: 96.8, unit: '%', label: '终端在线率', decimals: 1, prefix: '' },
    { value: 3.2, unit: 's', label: '平均响应延迟', decimals: 1, prefix: '< ' },
  ];

  return (
    <section id="架构" ref={sectionRef} className="reveal relative py-24 overflow-hidden">
      <GlowOrb className="bottom-0 left-0 w-[500px] h-[500px] bg-accent-500/5" />

      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <p className="text-xs font-mono tracking-[0.2em] text-primary-400 uppercase mb-3">
            Spatial Coordinate System
          </p>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50 mb-4">
            空间坐标系驱动的智能架构
          </h2>
          <p className="text-base text-foreground-400 max-w-2xl">
            所有终端在同一坐标系下共享感知数据，实现跨设备、跨场景的无缝协同。
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-0">
            {layers.map((layer, i) => (
              <div key={layer.name}>
                <div
                  className={`flex items-center gap-4 p-4 rounded-xl border ${layer.color} transition-all hover:translate-x-1 cursor-default`}
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-950/50 text-sm font-mono font-bold">
                    {layers.length - i}
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{layer.name}</p>
                    <p className="text-xs opacity-70 mt-0.5">{layer.desc}</p>
                  </div>
                </div>
                {i < layers.length - 1 && (
                  <div className="relative h-8 flex justify-center">
                    <div className="w-px h-full bg-gradient-to-b from-primary-400/30 via-primary-400/80 to-accent-400/30 arch-connector-line" />
                    <div
                      className="absolute left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-primary-400 shadow-[0_0_8px_rgba(217,162,78,0.6)] arch-connector-dot"
                      style={{ animationDelay: `${i * 0.35}s` }}
                    />
                    <span className="absolute left-[calc(50%+14px)] top-1/2 -translate-y-1/2 text-[10px] font-mono text-primary-400/40 hover:text-primary-400/80 transition-colors cursor-default whitespace-nowrap">
                      {connectorLabels[i]?.left}
                    </span>
                    <span className="absolute right-[calc(50%+14px)] top-1/2 -translate-y-1/2 text-[10px] font-mono text-accent-400/40 hover:text-accent-400/80 transition-colors cursor-default whitespace-nowrap">
                      {connectorLabels[i]?.right}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {stats.map((stat) => (
              <AnimatedStat key={stat.label} {...stat} enabled={visible} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}